import { useEffect, useRef, useState } from "react";
import { useAuth, useWallet } from "@crossmint/client-sdk-react-ui";
import { Link } from "react-router-dom";
import { CROSSMINT_ENVIRONMENT, CROSSMINT_DEPOSIT_PAIRS } from "./crossmintDepositConfig";
import { getSwapSigner } from "./getSwapSigner";
import { useCrossmintChainWallet } from "./useCrossmintChainWallet";
import api from "@/services/api";

const CHAINS = ["base", "arbitrum", "polygon", "bsc", "solana"]
  .filter((chain) => CROSSMINT_DEPOSIT_PAIRS.some((pair) => pair.chain === chain));
const LABELS = { base: "Base", arbitrum: "Arbitrum One", polygon: "Polygon", bsc: "BNB Chain", solana: "Solana" };

const backendAuthorization = async (wallet, chain, jwt) => {
  const response = await api.post("/users/swap/authorization", {
    chain, sourceWallet: wallet.address,
  }, { headers: { "x-crossmint-jwt": jwt }, timeout: 20000 });
  return response.data;
};

// Mounted inside both Crossmint providers and the Finu auth store. An old
// wallet is enrolled only after the user explicitly approves the operation.
const SwapSignerOnboarding = ({ userId }) => {
  const { jwt } = useAuth();
  const { wallet: baseWallet } = useWallet();
  const getChainWallet = useCrossmintChainWallet();
  const getChainWalletRef = useRef(getChainWallet);
  getChainWalletRef.current = getChainWallet;
  const checkedUser = useRef(null);
  const runId = useRef(0);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsSolanaUsdc, setNeedsSolanaUsdc] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [activeChain, setActiveChain] = useState(null);

  useEffect(() => {
    if (!userId || !jwt || !baseWallet || CROSSMINT_ENVIRONMENT !== "production") {
      checkedUser.current = null;
      runId.current += 1;
      setPending([]);
      setDismissed(false);
      return undefined;
    }
    if (checkedUser.current === userId) return undefined;
    checkedUser.current = userId;
    setPending([]);
    setDismissed(false);
    setError("");
    setNeedsSolanaUsdc(false);
    setActiveChain(null);
    const run = ++runId.current;
    let completed = false;
    const scan = async () => {
      if (!(await getSwapSigner("base"))) {
        if (run === runId.current) completed = true;
        return;
      }
      let existing;
      try {
        const response = await api.get("/users/swap/pending-authorizations", {
          headers: { "x-crossmint-jwt": jwt }, timeout: 30000,
        });
        existing = Array.isArray(response.data?.pending) ? response.data.pending : [];
      } catch {
        // Login remains available if wallet signing is not configured yet.
        if (run === runId.current) completed = true;
        return;
      }
      const awaiting = [];
      for (const item of existing) {
        const chain = item?.chain;
        if (!CHAINS.includes(chain)) continue;
        try {
          const signer = await getSwapSigner(chain);
          if (!signer) continue;
          const wallet = await getChainWalletRef.current(chain);
          if (wallet.address.toLowerCase() !== String(item.address).toLowerCase()) continue;
          const authorization = await backendAuthorization(wallet, chain, jwt);
          if (authorization?.authorized === true) continue;
          awaiting.push({
            chain, wallet, locator: signer.locator,
            verificationOnly: item.state === "pending" || authorization?.state === "pending",
          });
        } catch {
          // A failed check does not block sign-in. Swap checks this wallet again.
        }
      }
      if (run === runId.current) {
        setPending(awaiting);
        completed = true;
      }
    };
    void scan();
    return () => {
      runId.current += 1;
      if (!completed && checkedUser.current === userId) checkedUser.current = null;
    };
  }, [userId, jwt, baseWallet?.address]);

  const authorize = async () => {
    if (!pending.length || busy) return;
    setBusy(true);
    setError("");
    setNeedsSolanaUsdc(false);
    let remaining = [...pending];
    const total = remaining.length;
    let failed = false;
    try {
      for (const [index, item] of pending.entries()) {
        setActiveChain({ chain: item.chain, number: index + 1, total });
        try {
          // The backend checks the signer on this wallet and chain. A signer
          // approved on another network may already be ready here.
          let authorization = await backendAuthorization(item.wallet, item.chain, jwt);
          const needsRegistration = authorization?.state === "failed" ||
            (authorization?.state === "missing" && !item.verificationOnly);
          if (authorization?.authorized !== true && needsRegistration) {
            // Crossmint may ask for a separate OTP for each existing wallet.
            await item.wallet.addDelegatedSigner({ signer: item.locator });
            remaining = remaining.map((entry) => entry.chain === item.chain
              ? { ...entry, verificationOnly: true } : entry);
            setPending([...remaining]);
            authorization = await backendAuthorization(item.wallet, item.chain, jwt);
          }
          if (authorization?.authorized === true) {
            remaining = remaining.filter((entry) => entry.chain !== item.chain);
            setPending([...remaining]);
          } else {
            remaining = remaining.map((entry) => entry.chain === item.chain
              ? { ...entry, verificationOnly: authorization?.state === "pending" || entry.verificationOnly } : entry);
            setPending([...remaining]);
          }
        } catch (cause) {
          failed = true;
          const message = cause?.message || "";
          const insufficientUsdc = item.chain === "solana" && /insufficient USDC balance to cover transaction fees/i.test(message);
          if (insufficientUsdc) {
            const required = message.match(/Required:\s*([\d.]+)\s*USDC/i)?.[1];
            setNeedsSolanaUsdc(true);
            setError(`Your Solana wallet needs ${required ? `at least ${required} USDC` : "USDC"} on the Solana network to authorize swaps. Deposit USDC, then tap Authorize again. The fee may change before you retry.`);
          } else {
            setError(`${LABELS[item.chain]}: ${message || "Could not authorize this wallet. Please try again."}`);
          }
          break;
        }
      }
      if (remaining.length && !failed) {
        setError("Some wallet authorizations are still processing. Tap Authorize to check again.");
      }
    } catch (cause) {
      setError(cause?.message || "Could not authorize this wallet. Please try again.");
    } finally {
      setActiveChain(null);
      setBusy(false);
    }
  };

  if (!pending.length || dismissed) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 sm:items-center">
      <section role="dialog" aria-modal="true" aria-busy={busy} aria-labelledby="swap-signer-onboarding-title" className="w-full max-w-[440px] rounded-t-3xl bg-white p-6 shadow-xl sm:rounded-3xl">
        <h2 id="swap-signer-onboarding-title" className="text-xl font-semibold text-[#191919]">Set up easier swaps</h2>
        <p className="mt-3 text-sm leading-6 text-[#697586]">
          Authorize Finu to sign transactions from your wallets. This permission includes the ability to move funds. Each existing network may ask for its own code; future swaps on authorized networks will not.
        </p>
        <p className="mt-3 text-xs text-[#697586]">{activeChain
          ? `Authorizing ${activeChain.number} of ${activeChain.total}: ${LABELS[activeChain.chain]}`
          : `${pending.length} network${pending.length === 1 ? "" : "s"} still need authorization.`}</p>
        {!activeChain && <p className="mt-1 text-xs text-[#697586]">{pending.map((item) => LABELS[item.chain]).join(", ")}</p>}
        {error && <p role="alert" className="mt-4 rounded-xl bg-[#FFF5F3] p-3 text-sm text-[#B42318]">{error}</p>}
        {needsSolanaUsdc && <Link to="/deposit/wallet" onClick={() => setDismissed(true)} className="mt-3 inline-block text-sm font-semibold text-[#2855B8] underline">Get Solana USDC deposit address</Link>}
        <div className="mt-6 flex gap-3">
          <button type="button" disabled={busy} onClick={() => setDismissed(true)} className="min-h-12 flex-1 rounded-xl border border-[#D5D8DD] font-medium disabled:opacity-50">Later</button>
          <button type="button" disabled={busy} onClick={authorize} className="min-h-12 flex-1 rounded-xl bg-[#292B30] font-medium text-white disabled:opacity-50">Authorize</button>
        </div>
      </section>
    </div>
  );
};

export default SwapSignerOnboarding;
